import type {
  AiChatPayloadDataByType,
  AiHotel,
  AiHotelImage,
  AiHotelSearchParams,
} from "@/feature/ai-planner/types/ai-chat.types";
import { buildHotelRoutePreview } from "@/feature/hotel/checkout/lib/hotel-route-preview";
import { encodedHotelParams } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import type { IHotelList } from "@/feature/hotel/search/types/hotel-search-page-type";
import type { IHotelsSearchSchema } from "@/feature/hotel/search/types/hotel-search-type";

type HotelSearchResultData = AiChatPayloadDataByType<"HOTEL_SEARCH_RESULT">;

function asTrimmedString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function toFiniteNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function parseHotelDate(value: unknown) {
  const dateValue = asTrimmedString(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return null;
  }

  const parsed = new Date(`${dateValue}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeHotelImages(images?: AiHotelImage[]) {
  return (images ?? []).flatMap((image, index) => {
    const imageUrl = asTrimmedString(image?.image_url);

    if (!imageUrl) {
      return [];
    }

    return [
      {
        id: typeof image?.id === "number" ? image.id : index,
        image_url: imageUrl,
        image_caption: asTrimmedString(image?.image_caption),
      },
    ];
  });
}

function resolveHotelSearchParams(
  data?: HotelSearchResultData,
  hotel?: AiHotel,
): AiHotelSearchParams | null {
  const navigationParams = hotel?.navigation?.hotel_search_params;
  const payloadParams = data?.search_params;
  const location = navigationParams?.location ?? payloadParams?.location ?? data?.location;
  const date = {
    from:
      navigationParams?.date?.from ?? payloadParams?.date?.from ?? data?.checkin,
    to: navigationParams?.date?.to ?? payloadParams?.date?.to ?? data?.checkout,
  };
  const rooms = navigationParams?.rooms ?? payloadParams?.rooms ?? data?.rooms;

  if (!location && !date.from && !date.to && !rooms) {
    return null;
  }

  return {
    location,
    date,
    rooms,
  };
}

export function buildAiPlannerHotelSearchDataFromSearchParams(
  searchParams?: AiHotelSearchParams | null,
): IHotelsSearchSchema | null {
  const from = parseHotelDate(searchParams?.date?.from);
  const to = parseHotelDate(searchParams?.date?.to);
  const location = searchParams?.location;
  const rooms = searchParams?.rooms;

  if (!from || !to || !location || !Array.isArray(rooms)) {
    return null;
  }

  const locationName = asTrimmedString(location.name);
  if (!locationName || !rooms.length) {
    return null;
  }

  return {
    date: {
      from,
      to,
    },
    location: {
      id: typeof location.id === "number" ? location.id : 0,
      name: locationName,
      code: typeof location.code === "number" ? location.code : 0,
      type: location.type === "Hotel" ? "Hotel" : "City",
      city_code: typeof location.city_code === "number" ? location.city_code : 0,
      country_code: asTrimmedString(location.country_code),
      star_category:
        typeof location.star_category === "number"
          ? location.star_category
          : 0,
      country_name: asTrimmedString(location.country_name),
      rank: typeof location.rank === "number" ? location.rank : undefined,
    },
    rooms: rooms.map((room) => ({
      adults: typeof room?.adults === "number" && room.adults > 0 ? room.adults : 1,
      children:
        typeof room?.children === "number" && room.children >= 0
          ? room.children
          : 0,
      infants:
        typeof room?.infants === "number" && room.infants >= 0 ? room.infants : 0,
      children_ages: Array.isArray(room?.children_ages)
        ? room.children_ages.filter(
            (age): age is number => typeof age === "number" && age >= 0,
          )
        : [],
    })),
  };
}

export function buildAiPlannerHotelSearchData(
  data?: HotelSearchResultData,
  hotel?: AiHotel,
): IHotelsSearchSchema | null {
  return buildAiPlannerHotelSearchDataFromSearchParams(
    resolveHotelSearchParams(data, hotel),
  );
}

export function buildAiPlannerSelectedHotel(
  hotel: AiHotel,
  data?: HotelSearchResultData,
): IHotelList | null {
  const hotelDetailsQuery = hotel.navigation?.hotel_details_query;
  const hotelCode =
    asTrimmedString(hotel.hotel_code) || asTrimmedString(hotelDetailsQuery?.hcode);
  const searchId =
    asTrimmedString(hotel.search_id) ||
    asTrimmedString(hotelDetailsQuery?.search_id) ||
    asTrimmedString(data?.search_id);

  if (!hotelCode || !searchId) {
    return null;
  }

  const hotelRecord = hotel as Partial<IHotelList>;
  const parentSearchId =
    asTrimmedString(hotel.parent_search_id) ||
    asTrimmedString(hotelDetailsQuery?.parent_search_id) ||
    asTrimmedString(data?.parent_search_id) ||
    hotelRecord.parent_search_id;
  const resolvedSearchParams = resolveHotelSearchParams(data, hotel);
  const mainImage =
    hotelRecord.images?.main_image ||
    asTrimmedString(hotel.main_image) ||
    asTrimmedString(hotel.image_url) ||
    asTrimmedString(hotel.thumbnail);
  const additionalImages =
    hotelRecord.images?.additional_images?.length
      ? hotelRecord.images.additional_images
      : normalizeHotelImages(hotel.additional_images);
  const address =
    hotelRecord.contact_details?.address || asTrimmedString(hotel.address);
  const country =
    hotelRecord.contact_details?.country ||
    asTrimmedString(hotel.country) ||
    asTrimmedString(resolvedSearchParams?.location?.country_name);
  const boardingDetails =
    hotelRecord.details?.boarding_details ?? hotel.room_info?.boarding_details ?? [];
  const totalPrice = Number(hotel.total_price ?? hotel.price ?? 0);
  const price = Number(hotel.price ?? hotel.total_price ?? 0);
  const tax = Number(hotel.tax ?? 0);

  return {
    parent_search_id: parentSearchId || undefined,
    search_id: searchId,
    url: hotelRecord.url ?? "",
    hotel_code: hotelCode,
    acc_type: hotelRecord.acc_type ?? "",
    name: hotelRecord.name || hotel.name || "Hotel option",
    category:
      typeof hotel.category === "number"
        ? hotel.category
        : (hotelRecord.category ?? 0),
    description: hotelRecord.description ?? "",
    facilities: hotelRecord.facilities || hotel.facilities || "",
    geolocation: hotelRecord.geolocation ?? {
      latitude: toFiniteNumber(hotel.geolocation?.latitude),
      longitude: toFiniteNumber(hotel.geolocation?.longitude),
    },
    images: {
      main_image: mainImage,
      additional_images: additionalImages,
    },
    contact_details: hotelRecord.contact_details ?? {
      email: "",
      phone: "",
      fax: "",
      website_url: "",
      address,
      city_code: String(
        resolvedSearchParams?.location?.city_code ??
          resolvedSearchParams?.location?.code ??
          "",
      ),
      country,
    },
    currency: hotelRecord.currency || hotel.currency || "BDT",
    price_details: hotelRecord.price_details ?? {
      price,
      tax,
      total_price: totalPrice,
    },
    refundable: hotelRecord.refundable ?? Boolean(hotel.refundable),
    cancellation_policy:
      hotelRecord.cancellation_policy ??
      (hotel.refundable !== undefined
        ? {
            no_show_fee: 0,
            details: [],
            free_cancellation: Boolean(hotel.refundable),
          }
        : undefined),
    details: hotelRecord.details ?? {
      group_code: hotel.room_info?.group_code ?? "",
      boarding_details: boardingDetails,
      credit_deduction: "",
      has_promotions: false,
      includes_boarding: boardingDetails.length > 0,
      no_of_rooms: Array.isArray(resolvedSearchParams?.rooms)
        ? resolvedSearchParams.rooms.length
        : 1,
      pan_required: false,
      payment_type: [],
      rate_comments: {
        pax_comments: "",
      },
      rate_key: hotel.room_info?.rate_key ?? "",
      rate_type: "",
      room_code: "",
      supports_amendment: false,
      supports_cancellation: Boolean(hotel.refundable),
    },
    rooms: hotelRecord.rooms ?? [],
    safe2stay: hotelRecord.safe2stay ?? {},
    supp_cards: hotelRecord.supp_cards ?? [],
  };
}

export function buildAiPlannerHotelNavigationContext(
  hotel: AiHotel,
  data?: HotelSearchResultData,
) {
  const selectedHotel = buildAiPlannerSelectedHotel(hotel, data);
  const searchData = buildAiPlannerHotelSearchData(data, hotel);

  if (!selectedHotel || !searchData) {
    return null;
  }

  const routeQuery = new URLSearchParams(encodedHotelParams(searchData));
  routeQuery.set("searchId", selectedHotel.search_id || selectedHotel.hotel_code);

  if (selectedHotel.parent_search_id) {
    routeQuery.set("parent_search_id", selectedHotel.parent_search_id);
  }

  const previewHotel = buildHotelRoutePreview(selectedHotel);
  routeQuery.set("hotel_name", previewHotel.name);
  routeQuery.set("hotel_address", previewHotel.address);
  if (previewHotel.latitude !== null) {
    routeQuery.set("hotel_lat", String(previewHotel.latitude));
  }
  if (previewHotel.longitude !== null) {
    routeQuery.set("hotel_lng", String(previewHotel.longitude));
  }

  return {
    routePath: `/hotels/${encodeURIComponent(selectedHotel.hotel_code)}?${routeQuery.toString()}`,
    searchData,
    selectedHotel,
  };
}
